// frontend/src/components/Financeiro/ContabilidadeLivroRazao.js
// ✅ PERSONALIZAÇÃO DE MOEDA E FORMATAÇÃO DINÂMICA
// ✅ PAGINAÇÃO (16 itens por página) — mesmo estilo do FinanceiroDespesas
// ✅ SUPORTE AO TIPO "Mercadoria" (despesas de stock)
// 🔥 NOVO: configuracao (moeda+formatacao) + empresa vêm diretamente
//    da resposta do endpoint /api/financeiro/livro-razao — sem chamadas
//    duplicadas a /api/empresas/minha/atual
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Icon } from './SharedComponents';

const API = '/api' || 'http://localhost:3000';
const ITEMS_PER_PAGE = 16;

const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

// =============================================
// 🔥 CORES POR TIPO DE MOVIMENTO
// =============================================
const TIPO_CORES = {
    'Fatura': 'info',
    'Recibo': 'success',
    'Mercadoria': 'warning',
    'Compra de Stock': 'warning',
    'Despesa': 'danger',
    'Pagamento': 'success',
    'Nota de Crédito': 'muted'
};

export default function ContabilidadeLivroRazao() {
    const [ano, setAno] = useState(new Date().getFullYear());
    const [mes, setMes] = useState(new Date().getMonth());
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagina, setPagina] = useState(1);

    // 🔥 Config da empresa — começa com padrão
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({
        Authorization: `Bearer ${token}`
    }), [token]);

    // =============================================
    // 🔥 FORMATAR MOEDA (DINÂMICA)
    // =============================================
    const fmt = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    // =============================================
    // 🔥 FORMATAR DATA (DINÂMICA)
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mesNum = String(d.getMonth() + 1).padStart(2, '0');
        const anoNum = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mesNum);
        formato = formato.replace('YYYY', anoNum);
        formato = formato.replace('YY', String(anoNum).slice(-2));

        if (cfg.incluirHora) {
            const horas = String(d.getHours()).padStart(2, '0');
            const minutos = String(d.getMinutes()).padStart(2, '0');
            return `${formato} ${horas}:${minutos}`;
        }

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // 🔥 APLICAR CONFIGURAÇÃO DA EMPRESA (helper)
    // =============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    // =============================================
    // 🔥 ORDENAR MOVIMENTOS (mais recente primeiro)
    // =============================================
    const ordenarMovimentos = useCallback((lista) => {
        return [...lista].sort((a, b) => {
            const dataA = new Date(a.data || 0).getTime();
            const dataB = new Date(b.data || 0).getTime();
            if (dataB !== dataA) return dataB - dataA;
            return String(b.ref || '').localeCompare(String(a.ref || ''));
        });
    }, []);

    // =============================================
    // CARREGAR LIVRO RAZÃO (com config embutida)
    // =============================================
    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/financeiro/livro-razao`, {
                params: { ano, mes },
                headers
            });
            if (res.data.success) {
                setDados(res.data);
                // 🔥 Aplicar config (moeda + formatacao) da resposta
                aplicarConfiguracao(res.data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [ano, mes, headers, aplicarConfiguracao]);

    useEffect(() => { carregar(); }, [carregar]);

    // 🔥 reset da página quando muda ano/mês
    useEffect(() => { setPagina(1); }, [ano, mes]);

    // =============================================
    // 🔥 LISTA ORDENADA + PAGINADA
    // =============================================
    const movimentosOrdenados = useMemo(() => {
        const lista = dados?.movimentos || [];
        return ordenarMovimentos(lista);
    }, [dados, ordenarMovimentos]);

    const totalPaginas = Math.max(1, Math.ceil(movimentosOrdenados.length / ITEMS_PER_PAGE));

    const movimentosPaginados = useMemo(() => {
        const inicio = (pagina - 1) * ITEMS_PER_PAGE;
        return movimentosOrdenados.slice(inicio, inicio + ITEMS_PER_PAGE);
    }, [movimentosOrdenados, pagina]);

    const goToPage = (page) => {
        if (page < 1 || page > totalPaginas) return;
        setPagina(page);
    };

    // =============================================
    // 🔥 RENDER PAGINAÇÃO
    // =============================================
    const renderPagination = () => {
        const page = pagina;
        const pages = totalPaginas;
        const total = movimentosOrdenados.length;

        if (pages <= 1) return null;

        const startItem = (page - 1) * ITEMS_PER_PAGE + 1;
        const endItem = Math.min(page * ITEMS_PER_PAGE, total);

        const getPageNumbers = (currentPage, totalPages) => {
            const pagesArray = [];

            if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i++) {
                    pagesArray.push(i);
                }
                return pagesArray;
            }

            pagesArray.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                startPage = 2;
                endPage = Math.min(totalPages - 1, 4);
            }

            if (currentPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - 3);
                endPage = totalPages - 1;
            }

            if (startPage > 2) {
                pagesArray.push('...');
            }

            for (let i = startPage; i <= endPage; i++) {
                pagesArray.push(i);
            }

            if (endPage < totalPages - 1) {
                pagesArray.push('...');
            }

            pagesArray.push(totalPages);

            return pagesArray;
        };

        const pageNumbers = getPageNumbers(page, pages);

        return (
            <div className="cotacoes-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} movimentos</span>
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 1}
                    >
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {pageNumbers.map((p, index) => {
                            if (p === '...') {
                                return (
                                    <span key={`dots-${index}`} className="pagination-dots">
                                        …
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={p}
                                    className={`pagination-number ${page === p ? 'active' : ''}`}
                                    onClick={() => goToPage(p)}
                                >
                                    {p}
                                </button>
                            );
                        })}
                    </div>
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page + 1)}
                        disabled={page === pages}
                    >
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="fin-razao">
            <header className="fin-section-header">
                <div className="fin-section-title">
                    <Icon name="list_alt" size={24} color="var(--fin-primary)" />
                    <div>
                        <h2>Livro Razão</h2>
                        <p>{movimentosOrdenados.length} movimentos no período</p>
                    </div>
                </div>
                <div className="fin-section-actions">
                    <select
                        value={mes}
                        onChange={e => { setMes(parseInt(e.target.value)); setPagina(1); }}
                        className="fin-filter-select"
                    >
                        {meses.map((m, i) => <option key={i} value={i}>{m}</option>)}
                    </select>
                    <select
                        value={ano}
                        onChange={e => { setAno(parseInt(e.target.value)); setPagina(1); }}
                        className="fin-filter-select"
                    >
                        {[2023, 2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <button className="fin-btn-refresh" onClick={carregar}>
                        <Icon name="refresh" size={16} color="#fff" />
                    </button>
                </div>
            </header>

            {loading ? (
                <div className="fin-loading"><div className="fin-spinner" />A carregar...</div>
            ) : movimentosOrdenados.length === 0 ? (
                <div className="fin-empty">
                    <Icon name="list_alt" size={48} color="var(--fin-muted)" />
                    <h3>Sem movimentos</h3>
                    <p>Não há movimentos no período selecionado.</p>
                </div>
            ) : (
                <>
                    <div className="fin-table-wrapper">
                        <table className="fin-table">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Tipo</th>
                                    <th>Referência</th>
                                    <th>Descrição</th>
                                    <th>Contraparte</th>
                                    <th>Débito</th>
                                    <th>Crédito</th>
                                    <th>Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movimentosPaginados.map((m, i) => {
                                    const tipoNormalizado = String(m.tipo || '').trim();
                                    const isMercadoria =
                                        tipoNormalizado === 'Mercadoria' ||
                                        tipoNormalizado === 'Compra de Stock' ||
                                        String(m.descricao || '').toUpperCase().startsWith('MERCADORIA');

                                    const corBadge = TIPO_CORES[tipoNormalizado] || 'info';

                                    return (
                                        <tr key={m._id || i}>
                                            <td>{formatarData(m.data)}</td>
                                            <td>
                                                <span className={`fin-badge badge-${corBadge}`}>
                                                    {isMercadoria ? 'Mercadoria' : (m.tipo || '—')}
                                                </span>
                                            </td>
                                            <td><strong>{m.ref}</strong></td>
                                            <td>{m.descricao}</td>
                                            <td>{m.contraparte}</td>
                                            <td>{m.debito > 0 ? fmt(m.debito) : '—'}</td>
                                            <td>{m.credito > 0 ? fmt(m.credito) : '—'}</td>
                                            <td><strong>{fmt(m.saldo)}</strong></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {renderPagination()}
                </>
            )}
        </div>
    );
}